import { useEffect, useState, useRef } from "react";
import authService from "../../services/authService";
import { getTables, updateTableStatus } from "../../services/tableService";
import "../../styles/pages/Cashier/Dashboard.css";
import { getCategories, getItemsByCategory, getAllItems } from "../../services/menuService";
import { createOrder, getRunningOrders, getOrderDetails, getTableItems, settleTable, markItemServed, cancelItem, setItemQuantity, addBillItem, updateOrder, cancelOrder, getTodaysOrderCount, getBill, addItemToOrder, rebillOrder, getTodaysBills } from "../../services/orderService";
import RunningOrders from "../../components/Waiter/RunningOrders";
import CategoryTabs from "../../components/Waiter/CategoryTabs";
import MenuCard from "../../components/Waiter/MenuCard";
import CartItem from "../../components/Waiter/CartItem";
import BillModal from "../../components/Cashier/BillModal";
import TableBillModal from "../../components/Cashier/TableBillModal";
import { DayProvider, DayButton } from "../../components/DayControl";
import MenuAvailability from "../../components/Cashier/MenuAvailability";
import BillsHistory from "../../components/Cashier/BillsHistory";
import PrinterSetup from "../../components/Cashier/PrinterSetup";
import BillEditModal from "../../components/Cashier/BillEditModal";
import { registerNetwork } from "../../services/systemService";
import billingFormatService from "../../services/billingFormatService";
import settingsService from "../../services/settingsService";
import chargeService from "../../services/chargeService";
import { isBillOnlyDefault } from "../../utils/counterMode";
import {
    buildBillMessage,
    billFromSaved,
    whatsappNumber,
    whatsappUrl,
    openWhatsApp,
    getWhatsAppOverride,
    effectiveVia
} from "../../utils/whatsappBill";
import kitchenFormatService from "../../services/kitchenFormatService";
import printerSettingService from "../../services/printerSettingService";
import { DEFAULT_BILL_FORMAT } from "../../utils/billPrinter";
import { billTotals, autoChargesFor, money as roundMoney } from "../../utils/rates";
import { DEFAULT_KITCHEN_FORMAT } from "../../utils/kitchenPrinter";
// Prints straight to the configured printer; falls back to the browser dialog
// only when the till cannot print directly.
import { printBillNow, printKotNow } from "../../utils/printDispatch";
import { isSalon } from "../../utils/businessType";
import {
    DEFAULT_PRINTER_MODE,
    normalizePrinterMode,
    shouldPrintKotOnSend,
    shouldPrintKotWithBill
} from "../../utils/printerMode";
import usePersistentCart from "../../hooks/usePersistentCart";

function Dashboard() {
    // ── State ───────────────────────────────────────────────────────
    const [tables, setTables] = useState([]);
    const [selectedTable, setSelectedTable] = useState(null);
    // "Bill only" counter mode: rings up a walk-in sale WITHOUT sending anything
    // to the kitchen (for ready items — water, packed goods). It's a counter
    // order (no table) that skips every kitchen ticket.
    // A fresh counter sale starts in the till's default mode (Printer page →
    // "Default counter mode"); the cashier can still switch per bill.
    const [billOnly, setBillOnly] = useState(isBillOnlyDefault());
    const [categories, setCategories] = useState([]);
    const [menuItems, setMenuItems] = useState([]);
    const [allItems, setAllItems] = useState([]);    // whole menu (search across categories)
    const [selectedCategory, setSelectedCategory] = useState(null);
    // Survives a refresh and an Android WebView kill (hooks/usePersistentCart).
    const [cart, setCart] = usePersistentCart("inwallz_cart_cashier");
    // Parked counter / bill-only carts (the "Hold" feature). Persisted so a page
    // reload or a brief restart doesn't lose a held customer's order.
    const [heldOrders, setHeldOrders] = useState(() => {
        try { return JSON.parse(localStorage.getItem("inwallz_held_cashier") || "[]"); }
        catch { return []; }
    });
    const [runningOrders, setRunningOrders] = useState([]);
    const [todayOrders, setTodayOrders] = useState(0);
    const [editingOrder, setEditingOrder] = useState(null);
    const [showRunningOrders, setShowRunningOrders] = useState(false);
    // The selected table's already-ordered items, shown read-only for reference.
    const [previousItems, setPreviousItems] = useState([]);

    // Clear the read-only order view whenever no table is selected.
    useEffect(() => {
        if (!selectedTable) setPreviousItems([]);
    }, [selectedTable]);

    // Left sidebar + which full-screen view is showing.
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [activeView, setActiveView] = useState("dashboard");   // "dashboard" | "menu" | "bills" | "printer"
    // Bills screen: a settled bill opened for correction + reprint.
    const [editingBill, setEditingBill] = useState(null);        // the bill row
    const [editingBillItems, setEditingBillItems] = useState([]);
    const [editingBillCharged, setEditingBillCharged] = useState(0);  // amount already taken
    const [billEditBusy, setBillEditBusy] = useState(false);
    // Guards order submission so a double-tap can't fire two orders.
    const [orderBusy, setOrderBusy] = useState(false);
    const [showBill, setShowBill] = useState(false);
    const [billData, setBillData] = useState(null);
    // Table bill (waiter-requested) review + payment
    const [showTableBill, setShowTableBill] = useState(false);
    const [tableBillTarget, setTableBillTarget] = useState(null);
    const [tableBillItems, setTableBillItems] = useState([]);
    const [tableBillBusy, setTableBillBusy] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    // The signed-in cashier. This is not decoration: `cashierName` is what goes
    // out as `cashier_name` on every printed bill, so a placeholder here puts
    // the wrong person on the customer's receipt and on the day's audit trail.
    const currentUser = authService.getUser();
    const cashierName = currentUser?.full_name || currentUser?.username || "Cashier";
    const cashierId   = currentUser?.username || "";
    const [currentDate, setCurrentDate] = useState("");
    const [currentTime, setCurrentTime] = useState("");
    const [billFormat, setBillFormat] = useState(DEFAULT_BILL_FORMAT);
    const [kitchenFormat, setKitchenFormat] = useState(DEFAULT_KITCHEN_FORMAT);
    // Which printer setup the admin chose (Admin → Settings). It decides whether a
    // kitchen ticket prints when the order is sent, follows the bill, or never prints.
    const [printerMode, setPrinterMode] = useState(DEFAULT_PRINTER_MODE);
    // Which payment method is pre-selected on the bill screen (per restaurant,
    // Settings → Payments). Cash unless the owner set another default.
    const [paymentDefault, setPaymentDefault] = useState("Cash");
    // A NEW counter/parcel bill is created only at payment (create-on-pay), so a
    // bill the cashier starts but never settles leaves no draft behind. This holds
    // the order to create when payment is confirmed.
    const counterPayloadRef = useRef(null);
    const [restaurantInfo, setRestaurantInfo] = useState(null);
    // WhatsApp: the shop's message template + shop number + the owner's default
    // Web/App choice (Settings → Bills & WhatsApp). Bills → correct & reprint can
    // send the same bill on WhatsApp. This till's own Web/App override is set on
    // the Printer screen and stored per-PC (read live via getWhatsAppOverride).
    const [waSettings, setWaSettings] = useState({ whatsapp_template: "", shop_mobile: "", whatsapp_via: "web" });
    // Tap-to-add menu cards (Admin → Settings, per restaurant): no +/− on the card,
    // tap to add, change quantity in the cart. Lives in `settings`, read via
    // getRestaurant below. Off unless the owner turned it on (e.g. Tasty Travel).
    const [menuTapToAdd, setMenuTapToAdd] = useState(false);
    // Everything billed on top of the goods — GST, service charge, packing —
    // lives in Admin → Charges. A restaurant with none configured bills neither
    // tax nor service, which is the point of them being rows and not settings.
    const [charges, setCharges] = useState([]);

    // ── Functions ───────────────────────────────────────────────────
    function updateDateTime() {
        const now = new Date();
        setCurrentDate(now.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }));
        setCurrentTime(now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
    }

    const loadBillingFormat = async () => {
        try {
            const res = await billingFormatService.getBillingFormat();
            if (res.data?.success && res.data?.data) {
                if (res.data.data.format) setBillFormat(res.data.data.format);
                if (res.data.data.restaurant) setRestaurantInfo(res.data.data.restaurant);
            }
        } catch (e) {
            console.error("Failed to load bill format in cashier:", e);
        }
    };

    // WhatsApp message template + shop number + Web/App default (Settings, synced
    // from admin). Read-only here; used only to send a bill from Bills → reprint.
    const loadWhatsAppSettings = async () => {
        try {
            const res = await settingsService.getRestaurant();
            const s = res.data?.data || {};
            setWaSettings({
                whatsapp_template: s.whatsapp_template || "",
                shop_mobile: s.shop_mobile || "",
                whatsapp_via: s.whatsapp_via === "app" ? "app" : "web"
            });
            setMenuTapToAdd(Number(s.menu_tap_to_add) === 1);
        } catch (e) {
            // Not fatal — WhatsApp send falls back to the built-in template.
            console.error("Failed to load WhatsApp settings in cashier:", e);
        }
    };

    const loadCharges = async () => {
        try {
            const res = await chargeService.getCharges();
            if (res.data?.success) setCharges(res.data.data || []);
        } catch (e) {
            // Bill the goods rather than blocking the till. An unreachable
            // charge list means no tax line, which is visible on the screen.
            console.error("Failed to load charges in cashier:", e);
        }
    };

    const loadKitchenFormat = async () => {
        try {
            const res = await kitchenFormatService.getKitchenFormat();
            if (res.data?.success && res.data?.data?.format) {
                setKitchenFormat(res.data.data.format);
            }
        } catch (e) {
            console.error("Failed to load kitchen format in cashier:", e);
        }
    };

    const loadPrinterMode = async () => {
        try {
            const res = await printerSettingService.getPrinterSetting();
            if (res.data?.success) {
                setPrinterMode(normalizePrinterMode(res.data?.data?.setting?.printer_mode));
            }
        } catch (e) {
            // Fall back to the default setup rather than blocking the till.
            console.error("Failed to load printer mode in cashier:", e);
        }
    };

    // The pre-selected payment method (Settings → Payments, synced from admin).
    const loadPaymentDefault = async () => {
        try {
            const res = await settingsService.getPayments();
            const m = res.data?.data?.default_method;
            if (m) setPaymentDefault(m);
        } catch (e) {
            // Not fatal — the bill screen falls back to Cash.
            console.error("Failed to load payment default in cashier:", e);
        }
    };

    useEffect(() => {
        updateDateTime();
        loadTables();
        loadRunningOrders();
        loadCategories();
        loadAllItems();
        loadTodaysOrderCount();
        loadBillingFormat();
        loadKitchenFormat();
        loadPrinterMode();
        loadPaymentDefault();
        loadCharges();
        loadWhatsAppSettings();

        // Register this restaurant's WAN IP so waiter phones on the same WiFi
        // are recognised as "on the restaurant network" (cloud model).
        const pingNetwork = () => { registerNetwork().catch(() => {}); };
        pingNetwork();
        const netTimer = setInterval(pingNetwork, 60000);

        const statsTimer = setInterval(() => {
            loadTables();
            loadRunningOrders();
            loadTodaysOrderCount();
            loadCategories();   // pick up menu categories synced from the cloud
            loadPrinterMode();  // pick up a printer setup changed in Admin → Settings
            loadCharges();      // pick up a GST / service change made in Admin → Charges
        }, 10000);
        const refreshOnFocus = () => {
            loadTables();
            loadRunningOrders();
            loadTodaysOrderCount();
        };
        window.addEventListener("focus", refreshOnFocus);

        return () => {
            clearInterval(statsTimer);
            clearInterval(netTimer);
            window.removeEventListener("focus", refreshOnFocus);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!selectedCategory) return;
        loadMenuItems(selectedCategory);
        // Poll so availability changes from another cashier/admin appear live.
        const t = setInterval(() => loadMenuItems(selectedCategory), 4000);
        return () => clearInterval(t);
    }, [selectedCategory]);

    // While searching, keep the whole-menu list fresh (for live availability).
    useEffect(() => {
        if (!searchTerm.trim()) return;
        loadAllItems();
        const t = setInterval(loadAllItems, 4000);
        return () => clearInterval(t);
    }, [searchTerm]);

    // Keep the current-order list in sync so items the kitchen (or waiter) marks
    // served flip to served here within a few seconds, no refresh needed.
    useEffect(() => {
        if (!selectedTable || selectedTable.status !== "OCCUPIED") return;
        const t = setInterval(() => refreshTableItems(selectedTable.id), 4000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTable]);

    // When searching, look across the WHOLE menu; otherwise the selected category.
    const term = searchTerm.trim().toLowerCase();
    const filteredItems = (term ? allItems : menuItems).filter((item) =>
        item.item_name.toLowerCase().includes(term)
    );

    // One calculation for the whole screen — utils/rates mirrors
    // backend/utils/billing.js, so what the cashier sees is what gets stored.
    // A cart with no table (or a parcel table) is a takeaway, and charges can be
    // set to apply to one service type and not the other.
    const cartOrderType = (!selectedTable || selectedTable?.isParcel) ? "Takeaway" : "Dine-In";
    const cartAutoCharges = autoChargesFor(charges, cartOrderType);
    const subtotal   = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const cartTotals = billTotals(subtotal, cartAutoCharges);
    const gst        = cartTotals.tax;
    const serviceCharge = cartTotals.service_charge;
    const grandTotal = cartTotals.grand_total;

    const normalizeQuantity = (value) => {
        const parsed = Number(value);
        return !Number.isFinite(parsed) ? 0 : Math.round(parsed);
    };

    const getItemQuantityLimit = (item) => {
        const qty = Number(item.available_quantity);
        if (!Number.isFinite(qty)) return Number.POSITIVE_INFINITY;
        if (qty <= 1) return qty === 0 ? 0 : Number.POSITIVE_INFINITY;
        return qty;
    };

    const addToCart = (item) => {
        // No table needed — a cart with no table selected is a counter/walk-in order.
        const limit = getItemQuantityLimit(item);
        const currentTotal = cart
            .filter((c) => c.id === item.id)
            .reduce((s, c) => s + normalizeQuantity(c.quantity), 0);
        if (currentTotal >= limit) {
            alert(`Only ${limit} items available.`);
            return;
        }
        // Merge into the NEW line with NO note (noted lines stay separate).
        setCart((prev) => {
            const idx = prev.findIndex((c) => c.id === item.id && c.isNew && !c.note);
            if (idx !== -1) {
                const copy = [...prev];
                copy[idx] = { ...copy[idx], quantity: normalizeQuantity(copy[idx].quantity) + 1 };
                return copy;
            }
            return [...prev, { ...item, quantity: 1, note: "", isNew: true, lineId: `${Date.now()}-${Math.random()}` }];
        });
    };

    // "−" on a menu card removes one unit from the no-note line for that item.
    const removeOneFromCart = (item) => {
        setCart((prev) => {
            const idx = prev.findIndex((c) => c.id === item.id && c.isNew && !c.note);
            if (idx === -1) return prev;
            const line = prev[idx];
            if (normalizeQuantity(line.quantity) > 1) {
                const copy = [...prev];
                copy[idx] = { ...line, quantity: normalizeQuantity(line.quantity) - 1 };
                return copy;
            }
            return prev.filter((_, i) => i !== idx);
        });
    };

    // Set a per-line cooking note.
    const setLineNote = (lineId, note) => {
        setCart((prev) => prev.map((c) => (c.lineId === lineId ? { ...c, note } : c)));
    };

    const cartQtyFor = (itemId) =>
        cart.filter((c) => c.id === itemId).reduce((s, c) => s + normalizeQuantity(c.quantity), 0);

    const handleLogout = () => {
        authService.logout();
        window.location.href = "/";
    };

    const openRunningOrders = () => {
        if (blockIfParcelLocked()) return;
        setShowRunningOrders(true);
    };

    const loadCategories = async () => {
        try {
            const res = await getCategories();
            const cats = Array.isArray(res.data?.data) ? res.data.data : [];
            setCategories(cats);
            // Keep the current tab on live re-polls; only default on first load.
            // This updater runs later inside React's render — OUTSIDE this
            // try/catch — so the index must be fully optional-chained or an
            // undefined response crashes the whole till (white screen).
            setSelectedCategory((cur) => cur || (cats[0]?.id ?? null));
        } catch (e) { console.error(e); }
    };

    const loadTables = async () => {
        try {
            const data = await getTables();
            setTables(data.data);
        } catch (e) { console.error(e); }
    };

    // The unpaid order sitting on the selected table, whether or not it was
    // opened for editing. This is what "Cancel Order" acts on.
    const activeTableOrder = selectedTable
        ? runningOrders.find((o) => Number(o.table_id) === Number(selectedTable.id)) || null
        : null;

    const blockIfParcelLocked = () => {
        if (selectedTable?.isParcel && editingOrder) {
            alert("Please complete billing for the current Parcel order first.");
            return true;
        }
        return false;
    };

    // Pull a table's already-sent items (with their served flags) from the server.
    const refreshTableItems = async (tableId) => {
        try {
            const res = await getTableItems(tableId);
            setPreviousItems(
                (res.data.data || []).map((it) => ({
                    id: it.id,
                    item_name: it.item_name,
                    quantity: Number(it.quantity),
                    price: Number(it.price),
                    served: Number(it.served),
                }))
            );
        } catch (e) {
            setPreviousItems([]);
        }
    };

    const handleSelectTable = async (table) => {
        if (blockIfParcelLocked()) return;
        setSelectedTable(table);
        setBillOnly(false);   // a table order always goes to the kitchen
        await loadCategories();
        // Fresh cart for new items (each send = a new kitchen ticket).
        setCart([]);
        setEditingOrder(null);
        // Show the table's current (unpaid) order read-only.
        if (table.status === "OCCUPIED") {
            await refreshTableItems(table.id);
        } else {
            setPreviousItems([]);
        }
    };

    const loadMenuItems = async (categoryId) => {
        try {
            const res = await getItemsByCategory(categoryId);
            setMenuItems(res.data.data);
        } catch (e) { console.error(e); }
    };

    // Whole menu, used when searching across all categories.
    const loadAllItems = async () => {
        try {
            const res = await getAllItems();
            setAllItems(res.data.data || []);
        } catch (e) { console.error(e); }
    };

    // Cashier marks one item (order-item) served.
    const handleServeItem = async (item) => {
        setPreviousItems((prev) =>
            prev.map((it) => (it.id === item.id ? { ...it, served: 1 } : it))
        );
        try {
            await markItemServed(item.id);
            await loadRunningOrders();
        } catch (e) {
            setPreviousItems((prev) =>
                prev.map((it) => (it.id === item.id ? { ...it, served: 0 } : it))
            );
            alert("Could not mark the item as served.");
        }
    };

    const loadRunningOrders = async () => {
        try {
            const res = await getRunningOrders();
            setRunningOrders(res.data.data);
        } catch (e) { console.error("Error loading running orders:", e); }
    };

    const loadTodaysOrderCount = async () => {
        try {
            const res = await getTodaysOrderCount();
            setTodayOrders(Number(res.data.data) || 0);
        } catch (e) { console.error("Error loading today's order count:", e); }
    };

    const openOrder = async (order) => {
        try {
            const res = await getOrderDetails(order.id);
            const items = res.data.data.map((item, idx) => ({
                id: item.menu_item_id,
                menu_item_id: item.menu_item_id,
                item_name: item.item_name,
                quantity: normalizeQuantity(item.quantity),
                originalQuantity: normalizeQuantity(item.quantity),
                price: Number(item.price),
                gst: 5,
                available_quantity: 999,
                isNew: false,
                lineId: `existing-${item.menu_item_id}-${idx}`,
            }));
            setCart(items);
            setEditingOrder(order);
            setShowRunningOrders(false);
        } catch (e) {
            console.error(e);
            alert("Failed to load order.");
        }
    };

    const increaseQuantity = (lineId) => {
        setCart((prev) =>
            prev.map((item) =>
                item.lineId === lineId ? { ...item, quantity: normalizeQuantity(item.quantity) + 1 } : item
            )
        );
    };

    const decreaseQuantity = (lineId) => {
        setCart((prev) =>
            prev
                .map((item) =>
                    item.lineId === lineId ? { ...item, quantity: normalizeQuantity(item.quantity) - 1 } : item
                )
                .filter((item) => item.quantity > 0)
        );
    };

    const removeItem = (lineId) => {
        setCart((prev) => prev.filter((item) => item.lineId !== lineId));
    };

    // Merge the separate cart lines into one entry per menu item (used when
    // sending the order and when building the bill).
    const mergeCartItems = (list) =>
        Object.values(
            list.reduce((acc, it) => {
                const note = (it.note || "").trim();
                const k = `${it.id}|${note}`;   // same item + same note merges; different notes stay separate
                if (!acc[k]) {
                    acc[k] = {
                        menu_item_id: it.id,
                        item_name: it.item_name || it.name,
                        quantity: 0,
                        price: it.price,
                        gst: it.gst,
                        notes: note || null,
                    };
                }
                acc[k].quantity += normalizeQuantity(it.quantity);
                return acc;
            }, {})
        );

    const clearCart = () => {
        if (blockIfParcelLocked()) return;
        setCart([]);
    };

    // Keep the parked orders in localStorage so they survive a reload/restart.
    useEffect(() => {
        try { localStorage.setItem("inwallz_held_cashier", JSON.stringify(heldOrders)); }
        catch { /* private mode / quota — holds just won't persist */ }
    }, [heldOrders]);

    // ── Hold / park a counter or bill-only order ─────────────────────────
    // A walk-in is half rung-up when another customer needs billing NOW. Park the
    // current cart under a "Hold N" chip, clear the counter for the new order, and
    // resume the parked one later. Only counter / bill-only carts hold — a table
    // already parks itself as the table.
    const holdCurrentOrder = () => {
        if (blockIfParcelLocked()) return;
        if (cart.length === 0) { alert("Add items before holding the order."); return; }
        setHeldOrders((h) => [...h, { id: Date.now(), cart: cart.map((c) => ({ ...c })), billOnly }]);
        setCart([]);
        setSelectedTable(null);
        setBillOnly(isBillOnlyDefault());   // next fresh order opens in the default mode
        setEditingOrder(null);
    };

    // Resume a held order into the active cart and drop it from the held list.
    const resumeHeldOrder = (held) => {
        if (blockIfParcelLocked()) return;
        if (cart.length > 0) {
            alert("Finish or Hold the current order first, then open the held one.");
            return;
        }
        setSelectedTable(null);
        setBillOnly(Boolean(held.billOnly));
        setEditingOrder(null);
        setCart((held.cart || []).map((c) => ({ ...c })));
        setHeldOrders((h) => h.filter((x) => x.id !== held.id));
    };

    const discardHeldOrder = (id) => {
        if (!window.confirm("Discard this held order?")) return;
        setHeldOrders((h) => h.filter((x) => x.id !== id));
    };

    const handleChangeTable = async () => {
        if (blockIfParcelLocked()) return;
        if (cart.length > 0) {
            const ok = window.confirm("You have an unfinished order. Discard it?");
            if (!ok) return;
        }
        setSelectedTable(null);
        setBillOnly(false);
        setCart([]);
        setEditingOrder(null);
        await loadTables();
    };

    // "Bill Only" counter mode — a walk-in sale that is NOT sent to the kitchen.
    // Same as Counter, but flags the bill so no kitchen ticket is printed.
    const handleBillOnly = async () => {
        if (blockIfParcelLocked()) return;
        if (cart.length > 0) {
            const ok = window.confirm("You have an unfinished order. Discard it?");
            if (!ok) return;
        }
        setSelectedTable(null);
        setBillOnly(true);
        setCart([]);
        setEditingOrder(null);
        await loadTables();
    };

    const placeOrder = async () => {
        if (orderBusy) return;
        if (cart.length === 0) { alert("Please add items."); return; }
        if (!selectedTable) { alert("Please select a table first."); return; }

        setOrderBusy(true);
        // No order_number: the backend assigns it (utils/orderNumber.js, one
        // sequence per restaurant per day) and ignores anything sent here. The
        // till used to send `ORD-<milliseconds>` and then print that when the
        // response was not read, putting a number on paper that existed nowhere.
        const orderData = {
            waiter_id: 1,
            table_id: selectedTable?.id || null,
            order_type: selectedTable?.isParcel ? "Takeaway" : "Dine-In",
            items: mergeCartItems(cart),
        };
        // Was this table's order already sent to the kitchen (the waiter placed it
        // and the kitchen is on it), and we're only editing/re-saving it here?
        // Then it must NOT go to the kitchen again — re-firing a whole order the
        // kitchen has already made is the bug the counter kept hitting. A brand
        // new order (below) is the only thing that prints a KOT.
        const isUpdate = Boolean(editingOrder);
        try {
            let assignedOrderNumber = null;
            if (editingOrder) {
                const upd = await updateOrder(editingOrder.id, orderData);
                assignedOrderNumber = editingOrder.order_number;
                // If the order was already paid, the backend re-syncs the recorded
                // payment to the new total and tells us the difference to settle.
                const recon = upd.data?.data;
                const diff = Number(recon?.difference || 0);
                if (recon?.reconciled && Math.abs(diff) >= 0.01) {
                    alert(diff > 0
                        ? `Order updated. Collect ₹${diff.toFixed(2)} more from the customer.`
                        : `Order updated. Refund ₹${Math.abs(diff).toFixed(2)} to the customer.`);
                } else {
                    alert(selectedTable?.isParcel ? "Parcel Order Updated" : "Order Updated Successfully");
                }
                if (!selectedTable?.isParcel) {
                    setEditingOrder(null);
                }
            } else {
                const res = await createOrder(orderData);
                if (res.data?.data?.order_number) {
                    assignedOrderNumber = res.data.data.order_number;
                }
                if (selectedTable && selectedTable.id) {
                    await updateTableStatus(selectedTable.id, "OCCUPIED");
                    setSelectedTable({ ...selectedTable, status: "OCCUPIED" });
                    await loadTables();
                }
                alert(selectedTable?.isParcel ? "Parcel Order Sent To Kitchen" : "Order Sent To Kitchen");
                if (selectedTable?.isParcel) {
                    setEditingOrder({ id: res.data.data.order_id, order_number: res.data.data.order_number });
                }
            }

            // Print the KOT to the kitchen printer — only for a NEW order, and only
            // on the two-printer setup (the other setups use the Kitchen Display or
            // print the kitchen copy behind the bill; see utils/printerMode.js).
            // Editing an existing order never re-sends it to the kitchen.
            if (!isUpdate && shouldPrintKotOnSend(printerMode)) {
                printKotNow({
                    order: {
                        order_number: assignedOrderNumber,
                        order_type: selectedTable?.isParcel ? "Takeaway" : "Dine-In",
                        isParcel: Boolean(selectedTable?.isParcel),
                        tableName: selectedTable?.isParcel ? "PARCEL" : `Table ${selectedTable.table_number}`,
                        table_number: selectedTable?.table_number,
                        items: orderData.items,
                        cashier_name: cashierName,
                        date: currentDate,
                        time: currentTime
                    },
                    restaurant: restaurantInfo || {},
                    format: kitchenFormat || {}
                });
            }

            if (!selectedTable?.isParcel) {
                setCart([]);
                setSelectedTable(null);
                setEditingOrder(null);
            }
            updateDateTime();
            await loadTables();
            await loadRunningOrders();
            await loadTodaysOrderCount();
        } catch (error) {
            console.error("Order Error:", error);
            alert(error.response?.data?.message || error.friendlyMessage || "Failed to place order.");
        } finally {
            setOrderBusy(false);
        }
    };

    const handleCancelOrder = async () => {
        // Cancel whatever order is actually open on this table. Selecting a table
        // clears `editingOrder` (the cart starts empty for the next round), so
        // requiring it meant the button only ever appeared after going in through
        // Running Orders — the feature looked missing from the table view.
        const target = editingOrder || activeTableOrder;
        if (!target) { alert("There is no open order on this table."); return; }
        if (!window.confirm("Are you sure you want to cancel this order?")) return;
        try {
            await cancelOrder(target.id);
            if (selectedTable && selectedTable.id) {
                await updateTableStatus(selectedTable.id, "FREE");
                setSelectedTable({ ...selectedTable, status: "FREE" });
                await loadTables();
            }
            alert("Order Cancelled Successfully");
            setCart([]);
            setSelectedTable(null);
            setEditingOrder(null);
            await loadTables();
            await loadRunningOrders();
            await loadTodaysOrderCount();
        } catch (e) {
            console.error(e);
            alert("Failed to cancel order.");
        }
    };

    const handleProceedToBilling = async () => {
        if (orderBusy) return;
        if (cart.length === 0) { alert("Please add items."); return; }

        let orderId = editingOrder?.id;
        // Only ever the number the backend assigned. There is no local counter
        // to fall back on — a made-up number on a customer's bill cannot be
        // looked up, and came out different on every reprint.
        let assignedOrderNumber = editingOrder ? editingOrder.order_number : null;

        // A counter / walk-in order: no table, or a parcel. The printer setup treats
        // these differently from dine-in (option 3 prints their kitchen copy).
        const isTakeaway = !selectedTable || Boolean(selectedTable?.isParcel);

        // A NEW counter/parcel bill is created only at payment (ensureOrder below)
        // so an abandoned bill leaves nothing behind. A parcel goes to the kitchen
        // only after it's paid anyway, so nothing is lost by waiting. A NEW dine-in
        // billed directly still creates + sends to the kitchen now.
        const deferCreate = !orderId && isTakeaway;

        // New dine-in billed directly → create it now (this also sends it to the kitchen).
        if (!orderId && !deferCreate) {
            setOrderBusy(true);
            const orderData = {
                waiter_id: 1,
                table_id: selectedTable?.id || null,
                order_type: "Dine-In",
                items: mergeCartItems(cart),
            };
            try {
                const res = await createOrder(orderData);
                orderId = res.data.data.order_id;
                assignedOrderNumber = res.data.data.order_number || assignedOrderNumber;
                if (selectedTable && selectedTable.id) {
                    await updateTableStatus(selectedTable.id, "OCCUPIED");
                }
                if (shouldPrintKotOnSend(printerMode)) {
                    printKotNow({
                        order: {
                            order_number: assignedOrderNumber,
                            order_type: "Dine-In",
                            isParcel: false,
                            tableName: selectedTable ? `Table ${selectedTable.table_number}` : "Counter",
                            table_number: selectedTable?.table_number,
                            items: orderData.items,
                            cashier_name: cashierName,
                            date: currentDate,
                            time: currentTime
                        },
                        restaurant: restaurantInfo || {},
                        format: kitchenFormat || {}
                    });
                }
            } catch (error) {
                console.error("Order Error:", error);
                alert(error.response?.data?.message || error.friendlyMessage || "Failed to place order.");
                setOrderBusy(false);
                return;
            } finally {
                setOrderBusy(false);
            }
        }

        // Snapshot the parcel/counter order so it's created at payment, not before.
        counterPayloadRef.current = deferCreate ? {
            table_id: selectedTable?.id || null,
            // "Bill Only" counter sale — saved on the order so the Bills list and
            // reports can show "Bill Only" instead of "Counter".
            no_kitchen: billOnly ? 1 : 0,
            items: mergeCartItems(cart)
        } : null;

        setBillData({
            order_id: deferCreate ? undefined : orderId,
            order_number: deferCreate ? undefined : assignedOrderNumber,
            tableName: selectedTable ? (selectedTable.isParcel ? "PARCEL" : `Table ${selectedTable.table_number}`) : (billOnly ? "Counter (Bill only)" : "Counter"),
            isCounter: isTakeaway,
            // Bill-only: never print a kitchen ticket for this sale.
            noKitchen: billOnly,
            table_number: selectedTable?.table_number,
            items: mergeCartItems(cart),
            subtotal: Number(subtotal.toFixed(2)),
            gst: Number(gst.toFixed(2)),
            serviceCharge: Number(serviceCharge.toFixed(2)),
            // Standing charges (packing on takeaway, an AC charge) are part of
            // what is owed, so the counter bill has to carry them too — this
            // total was goods + tax + service and left them off.
            charges: cartTotals.charge_lines,
            // Named tax / service lines, so the bill screen and the receipt
            // print what this restaurant actually charges rather than "GST 5%".
            taxLines: [...cartTotals.tax_lines, ...cartTotals.service_lines],
            total: cartTotals.grand_total,
            // A counter / parcel bill the cashier rings up — name them on it. If a
            // waiter had opened this order, show that waiter instead.
            waiter_name: editingOrder && editingOrder.employee_role === "waiter"
                ? editingOrder.employee_name : undefined,
            cashier_name: (editingOrder && editingOrder.employee_role === "waiter")
                ? undefined : cashierName,
        });
        setShowBill(true);
    };

    // Create the counter/parcel order at PAYMENT (BillModal calls this on confirm),
    // from the snapshot taken when billing was opened. Returns the new id + number.
    const createCounterOrder = async () => {
        if (!counterPayloadRef.current) throw new Error("This bill has expired — reopen it.");
        const p = counterPayloadRef.current;
        const res = await createOrder({
            waiter_id: 1,
            table_id: p.table_id || null,
            order_type: "Takeaway",
            no_kitchen: p.no_kitchen || 0,
            items: p.items,
        });
        if (p.table_id) {
            try { await updateTableStatus(p.table_id, "OCCUPIED"); } catch (e) { /* non-fatal */ }
        }
        return { order_id: res.data.data.order_id, order_number: res.data.data.order_number };
    };

    // Option 3 of the printer setup: one printer prints two bills for a counter
    // order — the customer bill, then the kitchen bill. BillModal calls this once
    // the customer bill has gone out, passing how it went.
    const handleBillPrinted = (printedOrder, billResult) => {
        // The kitchen copy of a COUNTER / parcel order is printed only now, after
        // it's settled (paid): single-printer prints it behind the bill, and
        // two-printer sends it to the kitchen printer (its on-send KOT was held
        // back). Dine-in already printed its KOT when it was sent; the kitchen
        // display setup prints nothing.
        // "Bill only" sales are never sent to the kitchen.
        if (printedOrder?.noKitchen) return;
        const isCounter = Boolean(printedOrder?.isCounter);
        const wantsKot = isCounter &&
            (shouldPrintKotWithBill(printerMode, isCounter) || shouldPrintKotOnSend(printerMode));
        if (!wantsKot) return;

        const kot = {
            order_number: printedOrder.order_number,
            order_type: "Takeaway",
            isParcel: true,
            tableName: printedOrder.tableName || "Counter",
            table_number: printedOrder.table_number,
            items: printedOrder.items || [],
            cashier_name: cashierName,
            date: currentDate,
            time: currentTime
        };

        const sendKot = () => printKotNow({
            order: kot,
            restaurant: restaurantInfo || {},
            format: kitchenFormat || {}
        });

        // A direct print has already finished spooling by the time we get here, so
        // the kitchen copy can follow immediately. The browser-dialog fallback has
        // not, hence the pause — it lets the bill's dialog come up first so the two
        // still come out in order.
        if (billResult?.direct) sendKot();
        else setTimeout(sendKot, 900);
    };

    const handlePaymentSuccess = () => {
        setShowBill(false);
        setBillData(null);
        counterPayloadRef.current = null;
        setCart([]);
        setSelectedTable(null);
        setBillOnly(isBillOnlyDefault());   // back to this till's default mode
        setEditingOrder(null);
        alert("Bill Generated Successfully");
        updateDateTime();
        loadTables();
        loadRunningOrders();
        loadTodaysOrderCount();
    };

    // ── Table bill (waiter requested) — review / edit / take payment ──────
    const loadTableBillItems = async (tableId) => {
        try {
            const res = await getTableItems(tableId);
            setTableBillItems(
                (res.data.data || []).map((it) => ({
                    id: it.id, item_name: it.item_name,
                    quantity: Number(it.quantity), price: Number(it.price), served: Number(it.served),
                }))
            );
        } catch (e) { setTableBillItems([]); }
    };

    const openTableBill = async (table) => {
        setTableBillTarget(table);
        setShowTableBill(true);
        await loadTableBillItems(table.id);
    };

    const handleTableBillSetQty = async (rowId, qty) => {
        setTableBillBusy(true);
        try { await setItemQuantity(rowId, qty); await loadTableBillItems(tableBillTarget.id); }
        catch (e) { alert("Could not update the quantity."); }
        finally { setTableBillBusy(false); }
    };

    const handleTableBillRemove = async (rows) => {
        if (!window.confirm(`Remove ${rows[0]?.item_name || "this item"} from the bill?`)) return;
        setTableBillBusy(true);
        try { for (const r of rows) await cancelItem(r.id); await loadTableBillItems(tableBillTarget.id); }
        catch (e) { alert("Could not remove the item."); }
        finally { setTableBillBusy(false); }
    };

    const handleTableBillAdd = async (menuItem) => {
        setTableBillBusy(true);
        try { await addBillItem(tableBillTarget.id, menuItem.id, 1); await loadTableBillItems(tableBillTarget.id); }
        catch (e) { alert("Could not add the item."); }
        finally { setTableBillBusy(false); }
    };

    // Cashier can mark an item served from inside the bill modal.
    const handleTableBillServe = async (rows) => {
        const list = Array.isArray(rows) ? rows : [rows];
        setTableBillBusy(true);
        try {
            await Promise.all(list.map((r) => markItemServed(r.id)));
            await loadTableBillItems(tableBillTarget.id);
        }
        catch (e) { alert("Could not mark the item as served."); }
        finally { setTableBillBusy(false); }
    };

    // ── Bills screen: correct a settled bill, then reprint ───────────────
    const openBillForEdit = async (bill) => {
        setEditingBill(bill);
        setEditingBillCharged(Number(bill.paid_amount || bill.grand_total || 0));
        setEditingBillItems([]);
        // The menu is needed for "add a missed item".
        if (allItems.length === 0) loadAllItems();
        await refreshBillItems(bill.id);
    };

    const refreshBillItems = async (orderId) => {
        try {
            const res = await getOrderDetails(orderId);
            setEditingBillItems(res.data.data || []);
        } catch (e) {
            console.error("Bill items error:", e);
            alert("Could not load this bill's items.");
        }
    };

    const handleBillSetQty = async (itemId, quantity) => {
        setBillEditBusy(true);
        try {
            await setItemQuantity(itemId, quantity);
            await refreshBillItems(editingBill.id);
        } catch (e) {
            alert("Could not change the quantity.");
        } finally {
            setBillEditBusy(false);
        }
    };

    const handleBillRemove = async (rows) => {
        if (!window.confirm(`Remove ${rows[0]?.item_name || "this item"} from this bill?`)) return;
        setBillEditBusy(true);
        try {
            for (const r of rows) await cancelItem(r.id);
            await refreshBillItems(editingBill.id);
        } catch (e) {
            alert("Could not remove the item.");
        } finally {
            setBillEditBusy(false);
        }
    };

    const handleBillAdd = async (menuItem) => {
        setBillEditBusy(true);
        try {
            await addItemToOrder(editingBill.id, menuItem.id, 1);
            await refreshBillItems(editingBill.id);
        } catch (e) {
            alert("Could not add the item.");
        } finally {
            setBillEditBusy(false);
        }
    };

    // The shop, as the WhatsApp message wants it (name / number / address). The
    // number is the restaurant's own (restaurants.mobile), overridable in Settings.
    const shopForMessage = () => ({
        restaurant_name: restaurantInfo?.restaurant_name || currentUser?.restaurant_name,
        address: restaurantInfo?.address,
        mobile: waSettings.shop_mobile || restaurantInfo?.mobile
    });

    // What this till opens WhatsApp in: its own override, else the restaurant
    // default (Settings → Bills & WhatsApp).
    // Read the override live (set on the Printer screen, stored on this PC) so a
    // change there applies without a reload.
    const waVia = effectiveVia(getWhatsAppOverride(), waSettings.whatsapp_via);

    // Send an already-saved bill on WhatsApp — reuses the order (same number),
    // never creates a new one. Prompts for the number if the bill has none.
    const sendBillOnWhatsApp = (header) => {
        let phone = whatsappNumber(header.customer_mobile || editingBill?.customer_mobile);
        if (!phone) {
            const typed = window.prompt("Customer's WhatsApp number (with country code or 10 digits):", "");
            if (typed === null) return true;   // cancelled — not an error
            phone = whatsappNumber(typed);
            if (!phone) { alert("That doesn't look like a valid mobile number."); return false; }
        }
        const text = buildBillMessage(billFromSaved(header, editingBillItems), shopForMessage(), waSettings.whatsapp_template);
        if (!openWhatsApp(whatsappUrl(phone, text, waVia), waVia)) {
            alert("The browser blocked the WhatsApp window. Allow pop-ups for this page, then try again.");
            return false;
        }
        return true;
    };

    // Save the corrected totals (syncing the recorded payment), then hand the bill
    // over: print, WhatsApp, or both. Always the same order number (rebill reuses
    // the order) — it never creates a new bill.
    const deliverCorrectedBill = async (method, totals, { print = true, whatsapp = false } = {}) => {
        setBillEditBusy(true);
        try {
            const res = await rebillOrder(editingBill.id, method);
            const result = res.data.data;

            // Re-read the header so the receipt / message show the stored figures.
            let header = editingBill;
            try {
                header = (await getBill(editingBill.id)).data.data;
            } catch (e) {
                console.error("Bill header reload failed, printing from screen:", e);
            }

            if (print) {
                // Reprint straight to the thermal printer, exactly like the first
                // bill (printBillNow → POST /api/print). It falls back to the
                // browser dialog only if the printer can't be reached, instead of
                // ALWAYS opening that dialog as the old HTML reprint did.
                await printBillNow({
                    order: {
                        order_number: header.order_number,
                        tableName: header.table_name ? `Table ${header.table_name}` : "Counter",
                        table_number: header.table_name || undefined,
                        items: editingBillItems,
                        subtotal: totals.subtotal,
                        discount: totals.discount,
                        discount_label: totals.discountLabel,
                        tax: totals.gst,
                        service_charge: totals.service,
                        charges: totals.charges,
                        grand_total: totals.total,
                        payment_method: method,
                        cashier_name: cashierName,
                        isReprint: true
                    },
                    restaurant: restaurantInfo || {},
                    format: billFormat || {}
                });
                // WhatsApp (if asked) after the receipt has gone to paper.
                if (whatsapp) sendBillOnWhatsApp(header);
            } else if (whatsapp) {
                sendBillOnWhatsApp(header);
            }

            const diff = Number(result.difference || 0);
            if (Math.abs(diff) >= 0.01) {
                alert(
                    diff > 0
                        ? `Bill corrected. Collect ₹${diff.toFixed(2)} more from the customer.`
                        : `Bill corrected. Refund ₹${Math.abs(diff).toFixed(2)} to the customer.`
                );
            }

            closeBillEdit();
            await loadTodaysOrderCount();
        } catch (e) {
            console.error("Rebill error:", e);
            alert(e.response?.data?.message || e.friendlyMessage || "Could not save the corrected bill.");
        } finally {
            setBillEditBusy(false);
        }
    };

    // Bills → correct & reprint: paper only, or (with the option) WhatsApp / both.
    const handleBillReprint = (method, totals) => deliverCorrectedBill(method, totals, { print: true, whatsapp: false });
    // Save the correction without printing a new copy.
    const handleBillSaveOnly = (method, totals) => deliverCorrectedBill(method, totals, { print: false, whatsapp: false });
    const handleBillWhatsApp = (method, totals, opts = {}) =>
        deliverCorrectedBill(method, totals, { print: !!opts.print, whatsapp: true });

    // Bills → correct & reprint → "Reprint KOT": re-send only the picked items to
    // the KITCHEN printer (not the customer bill) — for when the kitchen ticket
    // didn't print fully (e.g. paper ran out). Prints in the normal KOT format.
    const handleReprintKotForBill = async (selectedItems) => {
        if (!editingBill || !selectedItems || selectedItems.length === 0) return;
        await printKotNow({
            order: {
                order_number: editingBill.order_number,
                order_type: editingBill.table_name ? "Dine-In" : "Takeaway",
                tableName: editingBill.table_name ? `Table ${editingBill.table_name}` : "Counter",
                table_number: editingBill.table_name || undefined,
                items: selectedItems,
                cashier_name: cashierName,
                date: currentDate,
                time: currentTime,
                isReprint: true
            },
            restaurant: restaurantInfo || {},
            format: kitchenFormat || {}
        });
    };

    // Bills list → WhatsApp: resend a saved bill without correcting it. Loads the
    // bill's items first (the list row has none), then opens WhatsApp.
    const sendSavedBillOnWhatsApp = async (bill) => {
        try {
            const [head, rows] = await Promise.all([getBill(bill.id), getOrderDetails(bill.id)]);
            const header = head.data.data;
            const items = rows.data.data || [];
            let phone = whatsappNumber(header.customer_mobile || bill.customer_mobile);
            if (!phone) {
                const typed = window.prompt("Customer's WhatsApp number (with country code or 10 digits):", "");
                if (typed === null) return;
                phone = whatsappNumber(typed);
                if (!phone) { alert("That doesn't look like a valid mobile number."); return; }
            }
            const text = buildBillMessage(billFromSaved(header, items), shopForMessage(), waSettings.whatsapp_template);
            if (!openWhatsApp(whatsappUrl(phone, text, waVia), waVia)) {
                alert("The browser blocked the WhatsApp window. Allow pop-ups for this page, then try again.");
            }
        } catch (e) {
            console.error("WhatsApp bill error:", e);
            alert("Could not load this bill.");
        }
    };

    const closeBillEdit = () => {
        setEditingBill(null);
        setEditingBillItems([]);
        setEditingBillCharged(0);
    };

    // Print the receipt (with the chosen payments) and settle the table.
    // `payments` is an array of { method, amount } supporting split payments.
    const generateTableBill = async (payments, finalTotal, selectedCharges = []) => {
        const table = tableBillTarget;
        setTableBillBusy(true);

        const items = tableBillItems;
        const paymentList = Array.isArray(payments) ? payments : [{ method: payments, amount: finalTotal }];
        const primaryMethod = paymentList[0]?.method || "Cash";

        // Same calculation the backend will run (utils/rates mirrors
        // backend/utils/billing.js): the charges this restaurant applies to
        // every dine-in bill, plus the ones the cashier picked.
        const sub = roundMoney(items.reduce((s, i) => s + Number(i.price) * Number(i.quantity), 0));
        const {
            tax: gstAmt,
            service_charge: svc,
            charge_lines: resolvedCharges,
            grand_total: computedTotal
        } = billTotals(sub, [...autoChargesFor(charges, "Dine-In"), ...selectedCharges]);

        const total = finalTotal || computedTotal;

        // Who took this order — the waiter who served, else the cashier at the counter.
        const tableOrder = runningOrders.find((o) => Number(o.table_id) === Number(table.id));
        const billedByWaiter = tableOrder && tableOrder.employee_role === "waiter" ? tableOrder.employee_name : null;
        const billedByCashier = billedByWaiter ? null : (tableOrder && tableOrder.employee_name) || cashierName;

        // Print the customer copy and clear the screen. Called on a normal settle
        // AND on the recovery path (a settle whose reply was lost) so the customer
        // always gets one bill and the cashier never sees a false failure.
        const printAndFinish = async (billNumber, methodLabel) => {
            printBillNow({
                order: {
                    order_number: billNumber,
                    tableName: `Table ${table.table_number}`,
                    table_number: table.table_number,
                    items,
                    subtotal: sub,
                    tax: gstAmt,
                    service_charge: svc,
                    charges: resolvedCharges,
                    grand_total: total,
                    payment_method: primaryMethod,
                    payments: paymentList,
                    waiter_name: billedByWaiter || undefined,
                    cashier_name: billedByCashier || undefined,
                    date: currentDate,
                    time: currentTime
                },
                restaurant: restaurantInfo || {},
                format: billFormat || {}
            });
            alert(`Table ${table.table_number} billed (${methodLabel}) & settled — now Available.`);
            setShowTableBill(false);
            setTableBillTarget(null);
            setTableBillItems([]);
            setSelectedTable(null);
            await loadTables();
            await loadRunningOrders();
            await loadTodaysOrderCount();
        };

        const label = paymentList.length > 1 ? paymentList.map((p) => p.method).join(" + ") : primaryMethod;

        try {
            // Settle BEFORE printing — the backend re-derives the total and can
            // refuse (item changed, something unserved); printing first would hand
            // a receipt for a sale that never recorded.
            const settled = await settleTable(table.id, paymentList, total, selectedCharges);
            const billNumber = settled?.data?.data?.order_number || `Table ${table.table_number}`;
            await printAndFinish(billNumber, label);
        } catch (e) {
            // A NO-RESPONSE error (timeout / lost reply on a busy moment) does NOT
            // mean the bill failed — the settle may have completed on the till. Re-
            // check the table: if it's now free, the bill went through, so recover
            // its number, print, and show success — instead of the scary "not sent"
            // that led staff to re-bill the same table.
            if (!e.response) {
                try {
                    const fresh = await getTables();
                    const t2 = (fresh.data || []).find((x) => Number(x.id) === Number(table.id));
                    const freed = t2 && !["OCCUPIED", "BILLING"].includes(String(t2.status || "").toUpperCase());
                    if (freed) {
                        let billNumber = `Table ${table.table_number}`;
                        try {
                            const bills = await getTodaysBills();
                            const b = (bills.data?.data || [])
                                .filter((x) => Number(x.table_id) === Number(table.id) && x.order_status === "Completed")
                                .sort((a, c) => c.id - a.id)[0];
                            if (b?.order_number) billNumber = b.order_number;
                        } catch { /* keep the fallback label */ }
                        await printAndFinish(billNumber, label);
                        return;
                    }
                } catch { /* fall through to the normal error */ }
            }
            // A real refusal (unserved item, total mismatch) still tells the cashier
            // exactly what to fix.
            alert(e.response?.data?.message || e.friendlyMessage || "Could not generate the bill.");
        } finally {
            setTableBillBusy(false);
        }
    };

    // ── UI computed values ──────────────────────────────────────────
    const availableCount = tables.filter((t) => t.status === "FREE").length;
    // grandTotal already includes tax and service (see cartTotals above).
    const displayTotal   = grandTotal;
    const billTables     = tables
        .filter((t) => t.needs_bill)
        .sort((a, b) => new Date(a.updated_at) - new Date(b.updated_at));

    // ── Render (desktop POS) ────────────────────────────────────────
    return (
        <DayProvider>
        <div className="cashier-app pos">

            {/* ══ LEFT SIDEBAR (drawer) ══ */}
            {sidebarOpen && <div className="pos-scrim" onClick={() => setSidebarOpen(false)} />}
            <aside className={`pos-drawer${sidebarOpen ? " open" : ""}`}>
                <div className="pos-drawer-head">
                    <span className="pos-drawer-logo">InWallz POS</span>
                    <button className="pos-drawer-x" onClick={() => setSidebarOpen(false)}>✕</button>
                </div>
                <nav className="pos-nav">
                    <button
                        className={`pos-nav-item${activeView === "dashboard" ? " active" : ""}`}
                        onClick={() => { setActiveView("dashboard"); setSidebarOpen(false); }}
                    >
                        🧾 Dashboard
                    </button>
                    <button
                        className={`pos-nav-item${activeView === "menu" ? " active" : ""}`}
                        onClick={() => { setActiveView("menu"); setSidebarOpen(false); }}
                    >
                        🍽 Menu
                    </button>
                    <button
                        className={`pos-nav-item${activeView === "bills" ? " active" : ""}`}
                        onClick={() => { setActiveView("bills"); setSidebarOpen(false); }}
                    >
                        🧾 Bills
                    </button>
                    <button
                        className={`pos-nav-item${activeView === "printer" ? " active" : ""}`}
                        onClick={() => { setActiveView("printer"); setSidebarOpen(false); }}
                    >
                        🖨 Printer & Settings
                    </button>
                </nav>
            </aside>

            {/* ══ TOP BAR ══ */}
            <header className="pos-topbar">
                <button className="pos-hamburger" onClick={() => setSidebarOpen((o) => !o)} title="Menu">
                    <span /><span /><span />
                </button>
                <div className="pos-brand">
                    <span className="pos-logo">{restaurantInfo?.restaurant_name || currentUser?.restaurant_name || "InWallz POS"}</span>
                    <span className="pos-open">● Open</span>
                </div>
                <div className="pos-stats">
                    <span className="pos-stat"><b>{todayOrders}</b> Today's</span>
                    <span className="pos-stat"><b>{runningOrders.length}</b> Active</span>
                    <span className="pos-stat"><b>{availableCount}</b> Free</span>
                </div>

                <div className="pos-topactions">
                    <button className="pos-bell" onClick={openRunningOrders} title="Running orders">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                        {runningOrders.length > 0 && <span className="pos-bell-dot">{runningOrders.length}</span>}
                    </button>
                    <div className="pos-time"><span className="pos-clock">{currentTime}</span><span className="pos-date">{currentDate}</span></div>
                    <div className="pos-user">
                        <div className="pos-avatar">{cashierName.charAt(0)}</div>
                        <div className="pos-user-info"><span className="pos-user-name">{cashierName}</span><span className="pos-user-id">{cashierId}</span></div>
                    </div>
                    <DayButton />
                    <button className="pos-logout" onClick={handleLogout}>Logout</button>
                </div>
            </header>

            {activeView === "menu" ? <MenuAvailability /> :
             activeView === "bills" ? <BillsHistory onOpenBill={openBillForEdit} onWhatsApp={isSalon() ? sendSavedBillOnWhatsApp : undefined} /> :
             activeView === "printer" ? <PrinterSetup /> : (
            <>
            {/* ══ TABLE BAR ══ */}
            <div className="pos-tablebar">
                <span className="pos-tablebar-label">Tables</span>
                <div className="pos-tables">
                    {/* Counter first, then a "Bill only" walk-in that never goes to
                        the kitchen, then the tables. */}
                    <button className={`pos-tchip counter${!selectedTable && !billOnly ? " sel" : ""}`} onClick={handleChangeTable} title="Walk-in order (sends to kitchen)">
                        <span className="pos-tchip-name">🧾</span>
                        <span className="pos-tchip-state">Counter</span>
                    </button>
                    <button className={`pos-tchip billonly${!selectedTable && billOnly ? " sel" : ""}`} onClick={handleBillOnly} title="Bill a ready item — does NOT send to the kitchen">
                        <span className="pos-tchip-name">💵</span>
                        <span className="pos-tchip-state">Bill Only</span>
                    </button>
                    {/* Parked (held) counter/bill-only orders — tap to resume, ✕ to discard. */}
                    {heldOrders.map((h, i) => (
                        <button key={h.id} className="pos-tchip hold" onClick={() => resumeHeldOrder(h)} title="Resume this held order">
                            <span className="pos-tchip-name">⏸</span>
                            <span className="pos-tchip-state">Hold{i + 1}</span>
                            <span
                                className="pos-tchip-held-x"
                                onClick={(e) => { e.stopPropagation(); discardHeldOrder(h.id); }}
                                title="Discard this held order"
                            >✕</span>
                        </button>
                    ))}
                    {tables.map((table) => {
                        const isFree = table.status === "FREE";
                        const isBilled = table.needs_bill;
                        const isSel = selectedTable?.id === table.id;
                        const cls = isFree ? "free" : isBilled ? "billed" : "occ";
                        return (
                            <button key={table.id} className={`pos-tchip ${cls}${isSel ? " sel" : ""}`} onClick={() => handleSelectTable(table)}>
                                <span className="pos-tchip-name">T{table.table_number}</span>
                                <span className="pos-tchip-state">{isFree ? "Free" : isBilled ? "Billed" : "Occupied"}</span>
                                {Number(table.total_items) > 0 && (
                                    <span className="pos-tchip-served">{Number(table.served_items)}/{Number(table.total_items)}</span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* ══ BILLS TO PRINT (waiter requests) ══ */}
            {billTables.length > 0 && (
                <div className="pos-printalert">
                    <span className="pos-printalert-title">🔔 Bills to print:</span>
                    {billTables.map((t) => (
                        <button key={t.id} className="pos-printalert-btn" onClick={() => openTableBill(t)}>
                            🧾 Bill T{t.table_number}
                        </button>
                    ))}
                </div>
            )}

            {/* ══ BODY: menu (left) + docked bill (right) ══ */}
            <div className="pos-body">

                {/* MENU */}
                <div className="pos-menu">
                    <div className="pos-menu-top">
                        <div className="pos-search">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                            <input type="text" placeholder="Search menu items..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                        </div>
                    </div>
                    <CategoryTabs categories={categories} selectedCategory={selectedCategory} onSelectCategory={(id) => { setSearchTerm(""); setSelectedCategory(id); }} />
                    <div className="pos-menu-grid">
                        {filteredItems.length === 0 ? (
                            <p className="pos-noitems">No items available</p>
                        ) : (
                            filteredItems.map((item) => (
                                <MenuCard
                                    key={item.id}
                                    item={item}
                                    quantity={cartQtyFor(item.id)}
                                    addToCart={addToCart}
                                    removeOneFromCart={removeOneFromCart}
                                    tapToAddOnly={menuTapToAdd}
                                />
                            ))
                        )}
                    </div>
                </div>

                {/* DOCKED BILL PANEL */}
                <aside className="pos-bill">
                    <div className="pos-bill-head">
                        <div className="pos-bill-headtext">
                            <span className="pos-bill-title">
                                {selectedTable ? `Table ${selectedTable.table_number}` : billOnly ? "💵 Bill Only (no kitchen)" : "🧾 Counter Order"}
                            </span>
                            <span className="pos-bill-sub">{editingOrder ? editingOrder.order_number : (selectedTable ? "New order" : "Walk-in — no table")}</span>
                        </div>
                        <div className="pos-bill-headbtns">
                            {/* Hold: park this counter/bill-only order to bill a sudden
                                new customer, then resume it from the Hold chip. */}
                            {!selectedTable && cart.length > 0 && (
                                <button className="pos-bill-hold" onClick={holdCurrentOrder} title="Hold this order and start a new one">⏸ Hold</button>
                            )}
                            {selectedTable && <button className="pos-bill-change" onClick={handleChangeTable} title="Change table">Change</button>}
                            {cart.length > 0 && <button className="pos-bill-clear" onClick={clearCart} title="Clear new items">✕</button>}
                        </div>
                    </div>

                    <div className="pos-bill-scroll">
                        {/* Current unpaid order for the table */}
                        {previousItems.length > 0 && (
                            <div className="pos-prev">
                                <div className="pos-prev-label">Current order (unpaid)</div>
                                {previousItems.map((it) => (
                                    <div key={it.id} className={`pos-prev-row${it.served ? " served" : ""}`}>
                                        <span className="pos-prev-name">{it.quantity}× {it.item_name}</span>
                                        {it.served
                                            ? <span className="pos-prev-served">✓ Served</span>
                                            : <button className="pos-prev-serve" onClick={() => handleServeItem(it)}>Serve</button>}
                                    </div>
                                ))}
                                <button className="pos-print-settle" onClick={() => openTableBill(selectedTable)}>🧾 Generate Bill</button>
                                <div className="pos-prev-divider">＋ New items (new kitchen ticket)</div>
                            </div>
                        )}

                        {/* New items being added */}
                        {cart.length === 0 ? (
                            <div className="pos-bill-empty">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
                                <p>Add items from the menu</p>
                            </div>
                        ) : (
                            cart.map((item) => (
                                <CartItem key={item.lineId} item={item} increaseQuantity={increaseQuantity} decreaseQuantity={decreaseQuantity} removeItem={removeItem} setNote={setLineNote} />
                            ))
                        )}
                    </div>

                    {/* Totals + actions (always docked at the bottom) */}
                    <div className="pos-bill-foot">
                        {/* Two decimals, not whole rupees: rounding each line to
                            the nearest rupee made the printed lines disagree with
                            the printed total (238 + 12 + 5 ≠ 254). */}
                        <div className="pos-tot-row"><span>Subtotal</span><span>₹{subtotal.toFixed(2)}</span></div>
                        {/* One line per charge the restaurant actually applies,
                            named as it is in Admin → Charges. Nothing configured,
                            nothing charged — no phantom GST line. */}
                        {[...cartTotals.tax_lines, ...cartTotals.service_lines, ...cartTotals.charge_lines].map((c, i) => (
                            <div className="pos-tot-row" key={`${c.charge_name}-${i}`}>
                                <span>{c.charge_name}</span><span>₹{c.amount.toFixed(2)}</span>
                            </div>
                        ))}
                        <div className="pos-tot-row grand"><span>Total</span><span>₹{displayTotal.toFixed(2)}</span></div>
                        <div className="pos-bill-actions">
                            {selectedTable && (
                                <button className="pos-send" onClick={placeOrder} disabled={cart.length === 0 || orderBusy}>
                                    {orderBusy ? "Sending..." : editingOrder ? "Update Order" : "Send to Kitchen"}
                                </button>
                            )}
                            <button className="pos-pay" onClick={handleProceedToBilling} disabled={cart.length === 0 || orderBusy}>
                                {orderBusy ? "Processing..." : selectedTable ? "Proceed to Billing →" : billOnly ? "Bill (No Kitchen) →" : "Send & Bill →"}
                            </button>
                        </div>
                        {selectedTable && (editingOrder || activeTableOrder) && <button className="pos-cancel" onClick={handleCancelOrder}>✕ Cancel Order</button>}
                    </div>
                </aside>
            </div>
            </>
            )}

            {/* Modals */}
            {showRunningOrders && (
                <RunningOrders runningOrders={runningOrders} closeOrders={() => setShowRunningOrders(false)} openOrder={openOrder} />
            )}
            {showTableBill && tableBillTarget && (
                <TableBillModal
                    table={tableBillTarget}
                    items={tableBillItems}
                    menuItems={allItems}
                    busy={tableBillBusy}
                    charges={charges}
                    defaultMethod={paymentDefault}
                    onSetQty={handleTableBillSetQty}
                    onRemoveGroup={handleTableBillRemove}
                    onAddItem={handleTableBillAdd}
                    onServe={handleTableBillServe}
                    onGenerate={generateTableBill}
                    onClose={() => { setShowTableBill(false); setTableBillTarget(null); setTableBillItems([]); }}
                />
            )}
            {showBill && billData && (
                <BillModal
                    order={billData}
                    restaurant={restaurantInfo}
                    format={billFormat}
                    charges={charges}
                    defaultMethod={paymentDefault}
                    ensureOrder={createCounterOrder}
                    onClose={() => setShowBill(false)}
                    onSuccess={handlePaymentSuccess}
                    onPrinted={handleBillPrinted}
                />
            )}
            {editingBill && (
                <BillEditModal
                    bill={editingBill}
                    items={editingBillItems}
                    menuItems={allItems}
                    busy={billEditBusy}
                    chargedTotal={editingBillCharged}
                    charges={charges}
                    onSetQty={handleBillSetQty}
                    onRemoveGroup={handleBillRemove}
                    onAddItem={handleBillAdd}
                    onReprint={handleBillReprint}
                    onSaveOnly={handleBillSaveOnly}
                    onReprintKot={handleReprintKotForBill}
                    onWhatsApp={isSalon() ? handleBillWhatsApp : undefined}
                    onClose={closeBillEdit}
                />
            )}
        </div>
        </DayProvider>
    );
}

export default Dashboard;

